import React, { useEffect } from "react";
import { Box, InputLabel, Stack, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";

import CriteriaRow from "../elements/CriteriaRow";
import FieldGroup from "../../../shared/FieldGroup";
import SectionCard from "../../../shared/SectionCard";
import type {
  BaselineProgressionCriteria,
  MaintenancePlan,
  ProgressCriteria,
  TargetFormValues,
} from "../TargetBuilderPanel.types";
import {
  CRITERIA_OUTCOME_TEXT,
  deriveCriteriaDirection,
  getThresholdUiConfigForUnit,
  getThresholdUnitOptions,
  normalizeThresholdUnitForDimension,
} from "../TargetBuilderPanel.types";

/**
 * Section 3 — Mastery criteria.
 *
 * Configures the phase-transition thresholds:
 *   - Baseline progression (→ intervention / → generalization probe)
 *   - Mastery (→ next phase)
 *   - Regression (→ previous phase)
 *   - Generalization (→ maintenance)
 *   - Maintenance plan (check-in cadence post-mastery)
 *
 * All progression criteria use a locked direction per the L-Model spec —
 * "below" for the baseline-to-intervention transition and regression,
 * "above" for everything else. The user only edits the consecutive-points
 * and percent values.
 */
const TargetFormSection3: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  const thresholdUnitOptions = getThresholdUnitOptions(
    values.l1_dimension,
    values.l3_context,
    values.teaching_procedure,
    values.target_type
  );

  const baselineToInterventionDirection = deriveCriteriaDirection(
    values.l1_dimension,
    values.target_type,
    "baseline.to_intervention"
  );
  const baselineToGeneralizationDirection = deriveCriteriaDirection(
    values.l1_dimension,
    values.target_type,
    "baseline.to_generalization"
  );
  const masteryDirection = deriveCriteriaDirection(
    values.l1_dimension,
    values.target_type,
    "mastery"
  );
  const regressionDirection = deriveCriteriaDirection(
    values.l1_dimension,
    values.target_type,
    "regression"
  );
  const generalizationDirection = deriveCriteriaDirection(
    values.l1_dimension,
    values.target_type,
    "generalization"
  );

  const updateCriteria = <K extends keyof TargetFormValues>(
    key: K,
    partial: Partial<TargetFormValues[K]>
  ) => {
    setFieldValue(key as string, { ...(values[key] as object), ...partial });
  };

  const updateBaselineSub = (
    key: keyof Pick<BaselineProgressionCriteria, "to_intervention" | "to_generalization">,
    partial: Partial<ProgressCriteria>
  ) => {
    setFieldValue("baseline_progression_criteria", {
      ...values.baseline_progression_criteria,
      [key]: { ...values.baseline_progression_criteria[key], ...partial },
    });
  };

  // Keep threshold units valid for the selected L1 dimension.
  useEffect(() => {
    const normalizeCriteria = (criteria: ProgressCriteria): ProgressCriteria => ({
      ...criteria,
      threshold_unit: normalizeThresholdUnitForDimension(
        values.l1_dimension,
        criteria.threshold_unit,
        values.l3_context,
        values.teaching_procedure,
        values.target_type
      ),
    });

    const nextBaseline = {
      to_intervention: normalizeCriteria(values.baseline_progression_criteria.to_intervention),
      to_generalization: normalizeCriteria(values.baseline_progression_criteria.to_generalization),
    };
    const nextMastery = normalizeCriteria(values.mastery_criteria);
    const nextRegression = normalizeCriteria(values.regression_criteria);
    const nextGeneralization = normalizeCriteria(values.generalization_criteria);

    const shouldUpdateBaseline =
      nextBaseline.to_intervention.threshold_unit !==
        values.baseline_progression_criteria.to_intervention.threshold_unit ||
      nextBaseline.to_generalization.threshold_unit !==
        values.baseline_progression_criteria.to_generalization.threshold_unit;
    const shouldUpdateMastery =
      nextMastery.threshold_unit !== values.mastery_criteria.threshold_unit;
    const shouldUpdateRegression =
      nextRegression.threshold_unit !== values.regression_criteria.threshold_unit;
    const shouldUpdateGeneralization =
      nextGeneralization.threshold_unit !== values.generalization_criteria.threshold_unit;

    if (shouldUpdateBaseline) {
      setFieldValue("baseline_progression_criteria", {
        ...values.baseline_progression_criteria,
        ...nextBaseline,
      });
    }
    if (shouldUpdateMastery) setFieldValue("mastery_criteria", nextMastery);
    if (shouldUpdateRegression) setFieldValue("regression_criteria", nextRegression);
    if (shouldUpdateGeneralization) setFieldValue("generalization_criteria", nextGeneralization);
  }, [values, setFieldValue]);

  const renderProgressCriteria = (
    field: "mastery_criteria" | "regression_criteria" | "generalization_criteria",
    direction: "above" | "below",
    outcomeText: string,
    exampleText: string
  ) => {
    const criteria = values[field] as ProgressCriteria;
    const thresholdUi = getThresholdUiConfigForUnit(
      values.l1_dimension,
      criteria.threshold_unit,
      values.l3_context,
      values.teaching_procedure,
      values.target_type
    );
    return (
      <Stack gap={4}>
        <CriteriaRow
          consecutivePoints={criteria.consecutive_points}
          thresholdValue={criteria.percent}
          direction={direction}
          outcomeText={outcomeText}
          thresholdSuffix={thresholdUi.suffix}
          thresholdAriaLabel={thresholdUi.ariaLabel}
          thresholdMin={thresholdUi.min}
          thresholdMax={thresholdUi.max}
          thresholdStep={thresholdUi.step}
          thresholdUnit={criteria.threshold_unit}
          thresholdUnitOptions={thresholdUnitOptions}
          lockDirection
          onConsecutiveChange={(v) => updateCriteria(field, { consecutive_points: v } as any)}
          onThresholdChange={(v) => updateCriteria(field, { percent: v } as any)}
          onThresholdUnitChange={(unit) => updateCriteria(field, { threshold_unit: unit } as any)}
          onDirectionChange={(d) => updateCriteria(field, { direction: d } as any)}
        />

        <Typography variant="caption" color="text.secondary">
          {exampleText}
        </Typography>

        <FieldGroup label="Additional notes (optional)" labelVariant="caption">
          <TextField
            multiline
            minRows={2}
            fullWidth
            value={criteria.notes}
            onChange={(e) => updateCriteria(field, { notes: e.target.value } as any)}
            aria-label={`${field} notes`}
          />
        </FieldGroup>
      </Stack>
    );
  };

  const maintenance = values.maintenance_plan;
  const updateMaintenance = (partial: Partial<MaintenancePlan>) =>
    setFieldValue("maintenance_plan", { ...maintenance, ...partial });

  const baselineInterventionThresholdUi = getThresholdUiConfigForUnit(
    values.l1_dimension,
    values.baseline_progression_criteria.to_intervention.threshold_unit,
    values.l3_context,
    values.teaching_procedure,
    values.target_type
  );

  const baselineGeneralizationThresholdUi = getThresholdUiConfigForUnit(
    values.l1_dimension,
    values.baseline_progression_criteria.to_generalization.threshold_unit,
    values.l3_context,
    values.teaching_procedure,
    values.target_type
  );

  return (
    <SectionCard
      id="target-section-criteria"
      title="Section 3 — Mastery criteria"
      description="Thresholds for mastery, regression, maintenance, and generalization"
    >
      {/* Baseline Progression — two locked sub-criteria + shared notes */}
      <FieldGroup
        label="Baseline Progression Criteria"
        description="When should this target advance from baseline?"
        required
      >
        <Stack gap={4} mt={4}>
          <Stack gap={2}>
            <InputLabel component="label" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              Move to Intervention when:
            </InputLabel>

            <CriteriaRow
              consecutivePoints={
                values.baseline_progression_criteria.to_intervention.consecutive_points
              }
              thresholdValue={values.baseline_progression_criteria.to_intervention.percent}
              direction={baselineToInterventionDirection}
              outcomeText={CRITERIA_OUTCOME_TEXT.baselineToIntervention}
              thresholdSuffix={baselineInterventionThresholdUi.suffix}
              thresholdAriaLabel={baselineInterventionThresholdUi.ariaLabel}
              thresholdMin={baselineInterventionThresholdUi.min}
              thresholdMax={baselineInterventionThresholdUi.max}
              thresholdStep={baselineInterventionThresholdUi.step}
              thresholdUnit={values.baseline_progression_criteria.to_intervention.threshold_unit}
              thresholdUnitOptions={thresholdUnitOptions}
              lockDirection
              onConsecutiveChange={(v) =>
                updateBaselineSub("to_intervention", { consecutive_points: v })
              }
              onThresholdChange={(v) => updateBaselineSub("to_intervention", { percent: v })}
              onThresholdUnitChange={(unit) =>
                updateBaselineSub("to_intervention", { threshold_unit: unit })
              }
              onDirectionChange={(d) => updateBaselineSub("to_intervention", { direction: d })}
            />

            <Typography variant="caption" color="text.secondary">
              {values.target_type === "behavior-reduction"
                ? "Target behavior indicates intervention is needed"
                : "Client does not yet have the skill"}
            </Typography>
          </Stack>

          <Stack gap={2}>
            <InputLabel component="label" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              Move to Generalization (probe mastery) when:
            </InputLabel>
            <CriteriaRow
              consecutivePoints={
                values.baseline_progression_criteria.to_generalization.consecutive_points
              }
              thresholdValue={values.baseline_progression_criteria.to_generalization.percent}
              direction={baselineToGeneralizationDirection}
              outcomeText={CRITERIA_OUTCOME_TEXT.baselineToGeneralization}
              thresholdSuffix={baselineGeneralizationThresholdUi.suffix}
              thresholdAriaLabel={baselineGeneralizationThresholdUi.ariaLabel}
              thresholdMin={baselineGeneralizationThresholdUi.min}
              thresholdMax={baselineGeneralizationThresholdUi.max}
              thresholdStep={baselineGeneralizationThresholdUi.step}
              thresholdUnit={values.baseline_progression_criteria.to_generalization.threshold_unit}
              thresholdUnitOptions={thresholdUnitOptions}
              lockDirection
              onConsecutiveChange={(v) =>
                updateBaselineSub("to_generalization", { consecutive_points: v })
              }
              onThresholdChange={(v) => updateBaselineSub("to_generalization", { percent: v })}
              onThresholdUnitChange={(unit) =>
                updateBaselineSub("to_generalization", { threshold_unit: unit })
              }
              onDirectionChange={(d) => updateBaselineSub("to_generalization", { direction: d })}
            />
            <Typography variant="caption" color="text.secondary">
              {values.target_type === "behavior-reduction"
                ? "Behavior already at desired low level (probe generalization)"
                : "Client already demonstrates the skill (probe mastered)"}
            </Typography>
          </Stack>

          {/* Shared notes textarea — persisted in to_intervention.notes to avoid
              an API shape change. to_generalization.notes is intentionally
              kept in sync until the backend exposes a top-level notes field. */}
          <FieldGroup label="Additional notes (optional)" labelVariant="caption">
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={values.baseline_progression_criteria.to_intervention.notes}
              onChange={(e) => {
                const next = e.target.value;
                setFieldValue("baseline_progression_criteria", {
                  ...values.baseline_progression_criteria,
                  to_intervention: {
                    ...values.baseline_progression_criteria.to_intervention,
                    notes: next,
                  },
                  to_generalization: {
                    ...values.baseline_progression_criteria.to_generalization,
                    notes: next,
                  },
                });
              }}
              aria-label="Baseline progression notes"
            />
          </FieldGroup>
        </Stack>
      </FieldGroup>

      <FieldGroup
        label="Mastery Criteria"
        description="When is this target considered mastered?"
        required
      >
        {renderProgressCriteria(
          "mastery_criteria",
          masteryDirection,
          CRITERIA_OUTCOME_TEXT.mastery,
          "e.g., 3 consecutive data points at threshold"
        )}
      </FieldGroup>

      <FieldGroup
        label="Regression Criteria"
        description="When should this target return to a previous phase?"
        required
      >
        {renderProgressCriteria(
          "regression_criteria",
          regressionDirection,
          CRITERIA_OUTCOME_TEXT.regression,
          "e.g., 3 consecutive data points crossing regression threshold"
        )}
      </FieldGroup>

      <FieldGroup
        label="Generalization Criteria"
        description="When should this target advance from generalization to maintenance?"
        required
      >
        {renderProgressCriteria(
          "generalization_criteria",
          generalizationDirection,
          CRITERIA_OUTCOME_TEXT.generalization,
          "e.g., threshold met consistently across generalization settings"
        )}
      </FieldGroup>

      <FieldGroup
        label="Maintenance Criteria"
        description="How will this skill be maintained after mastery?"
        required
      >
        <Stack gap={2}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
            <Typography variant="body2">Present target every</Typography>

            <Box sx={{ width: 72, flexShrink: 0 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={maintenance.first_month_every_n_sessions}
                onChange={(e) =>
                  updateMaintenance({
                    first_month_every_n_sessions:
                      e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
                inputProps={{
                  min: 1,
                  "aria-label": "First month every N sessions",
                }}
                sx={{ "& input": { textAlign: "center", padding: "8px 6px" } }}
              />
            </Box>

            <Typography variant="body2">sessions for first month and every</Typography>

            <Box sx={{ width: 72, flexShrink: 0 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={maintenance.next_three_months_every_n_sessions}
                onChange={(e) =>
                  updateMaintenance({
                    next_three_months_every_n_sessions:
                      e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
                inputProps={{
                  min: 1,
                  "aria-label": "Next three months every N sessions",
                }}
                sx={{ "& input": { textAlign: "center", padding: "8px 6px" } }}
              />
            </Box>
            <Typography variant="body2">sessions for next 3 months</Typography>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            e.g., every 3 sessions for first month, every 5 sessions after
          </Typography>

          <FieldGroup label="Additional notes (optional)" labelVariant="caption">
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={maintenance.notes}
              onChange={(e) => updateMaintenance({ notes: e.target.value })}
              aria-label="Maintenance notes"
            />
          </FieldGroup>
        </Stack>
      </FieldGroup>
    </SectionCard>
  );
};

export default TargetFormSection3;
